/**
 * Workspace detection and hub resolution for workspace-aware CLI commands.
 *
 * Hub folders live directly under the resolved "hubs dir" — `wayai-ws/hubs/`
 * on the current layout, or the legacy `workspace/` for un-migrated repos (see
 * `layout.ts` / `resolveLayout`). Each hub folder has a `hub.yaml` (or legacy
 * `wayai.yaml`) file. `WorkspaceInfo.workspaceDir` IS that hubs dir.
 */

import { execFileSync } from 'node:child_process';
import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { slugify, UUID_RE } from './utils.js';
import { resolveLayout, hubsDirLabel, isDirectory, isRealDirectory } from './layout.js';
import { isSymlink, isUnder } from './fs-safety.js';

export interface HubLocation {
  hubFolder: string;
  hubId: string;
  hubEnvironment: string;
}

/** A hub folder where hub.yaml exists but has no hub_id yet (not created on platform). */
export interface NewHubLocation {
  hubFolder: string;
  hubName: string;
  hubType?: string;
}

export interface WorkspaceInfo {
  gitRoot: string;
  workspaceDir: string;
}

/**
 * Find the git repository root. Returns null if not inside a git repo.
 */
export function findGitRoot(): string | null {
  try {
    return execFileSync('git', ['rev-parse', '--show-toplevel'], {
      encoding: 'utf-8',
      stdio: ['pipe', 'pipe', 'pipe'],
    }).trim();
  } catch {
    return null;
  }
}

/**
 * Cheap, non-spawning repo-root finder for hot paths (the update nudge runs on every command).
 * Walks up from cwd looking for a `.git` entry — a directory in a normal clone, a file in a git
 * worktree, both matched by `existsSync`. Unlike `findGitRoot`, this spawns no `git` subprocess
 * (and needs no `git` on PATH). Returns null when not inside a repo.
 */
export function findRepoRootSync(): string | null {
  let current = process.cwd();
  while (true) {
    if (fs.existsSync(path.join(current, '.git'))) return current;
    const parent = path.dirname(current);
    if (parent === current) return null;
    current = parent;
  }
}

/**
 * Detect the hubs directory for the active layout (`wayai-ws/hubs/`, or the
 * legacy `workspace/`). Returns null if not in a git repo or the dir doesn't
 * exist yet (fresh repo before the first pull/init).
 */
export function detectWorkspace(): WorkspaceInfo | null {
  const gitRoot = findGitRoot();
  if (!gitRoot) return null;

  const { hubsDir } = resolveLayout(gitRoot);
  if (!fs.existsSync(hubsDir) || !fs.statSync(hubsDir).isDirectory()) {
    return null;
  }

  return { gitRoot, workspaceDir: hubsDir };
}

/**
 * Resolve the active hubs directory for a workspace-scoped command, exiting with
 * a clear message when not inside a git repo. Callers that want the layout
 * deprecation notice run `warnLayoutOnce(findGitRoot())` separately. Shared by
 * pull/push/diff so the resolution + "Not inside a git repository" exit can't drift.
 */
export function resolveWorkspaceDir(): string {
  const workspace = detectWorkspace();
  if (workspace) return workspace.workspaceDir;

  const gitRoot = findGitRoot();
  if (!gitRoot) {
    console.error('Not inside a git repository.');
    process.exit(1);
  }
  return resolveLayout(gitRoot).hubsDir;
}

/**
 * Read hub metadata from a hub.yaml (or wayai.yaml) file.
 * Returns null if the file doesn't exist or lacks required fields.
 */
function readHubYaml(yamlPath: string): { hubId: string; hubEnvironment: string } | null {
  // A symlinked hub.yaml never identifies a hub — its target would. Only the file itself is
  // checked here: every caller vouches for the folder — the scans lstat it
  // (`isRealDirectory`), and `findEnclosing*` walks `process.cwd()`, a physical path.
  if (!fs.existsSync(yamlPath) || isSymlink(yamlPath)) return null;

  try {
    const config = yaml.load(fs.readFileSync(yamlPath, 'utf-8')) as { hub_id?: string; hub_environment?: string } | undefined;
    if (!config?.hub_id || !config.hub_environment) return null;
    return { hubId: config.hub_id, hubEnvironment: config.hub_environment };
  } catch {
    return null;
  }
}

/** Scan `workspace/<hub>/hub.yaml` for hubs already created on the platform. */
export function scanWorkspaceHubs(workspaceDir: string): HubLocation[] {
  const hubs: HubLocation[] = [];

  for (const top of safeReaddir(workspaceDir)) {
    const topDir = path.join(workspaceDir, top);
    // lstat: a symlinked hub folder is never admitted as a hub (it would carry every
    // command's reads and writes to the link target).
    if (!isRealDirectory(topDir)) continue;

    const meta = readHubYaml(path.join(topDir, 'hub.yaml'))
              || readHubYaml(path.join(topDir, 'wayai.yaml'));
    if (meta) {
      hubs.push({ hubFolder: topDir, hubId: meta.hubId, hubEnvironment: meta.hubEnvironment });
    }
  }

  return hubs;
}

/** A hub folder is editable source only when it's a preview. Production folders
 *  on disk are read-only mirrors (push is blocked for production). */
export function isPreviewHub(hub: HubLocation): boolean {
  return hub.hubEnvironment === 'preview';
}

/**
 * Drop read-only production mirror folders. Apply this at every AUTO-SELECT site
 * (no explicit `--hub`, no enclosing folder) so a preview + its mirror still
 * resolves to the single preview. Explicit selectors and by-id/by-name lookups
 * intentionally bypass this — a production mirror is still directly addressable
 * (e.g. to refresh it).
 */
export function filterPreviewHubs(hubs: HubLocation[]): HubLocation[] {
  return hubs.filter(isPreviewHub);
}

/**
 * Read hub metadata from a hub.yaml that has NO hub_id yet (new hub).
 * Returns the hub name and optional hub_type. Returns null if hub_id is present or hub.name is missing.
 */
function readNewHubYaml(yamlPath: string): { hubName: string; hubType?: string } | null {
  if (!fs.existsSync(yamlPath) || isSymlink(yamlPath)) return null;

  try {
    const config = yaml.load(fs.readFileSync(yamlPath, 'utf-8')) as { hub_id?: string; hub?: { name?: string; hub_type?: string } } | undefined;
    // Skip if hub_id is present (already created on platform)
    if (config?.hub_id) return null;
    // Must have hub.name to be a valid new hub
    if (!config?.hub?.name) return null;
    return { hubName: config.hub.name, hubType: config.hub.hub_type };
  } catch {
    return null;
  }
}

/** Scan `workspace/<hub>/hub.yaml` for hubs not yet created on the platform (no `hub_id`). */
export function scanNewHubs(workspaceDir: string): NewHubLocation[] {
  const hubs: NewHubLocation[] = [];

  for (const top of safeReaddir(workspaceDir)) {
    const topDir = path.join(workspaceDir, top);
    // lstat: a symlinked hub folder is never admitted as a hub (it would carry every
    // command's reads and writes to the link target).
    if (!isRealDirectory(topDir)) continue;

    const meta = readNewHubYaml(path.join(topDir, 'hub.yaml'))
              || readNewHubYaml(path.join(topDir, 'wayai.yaml'));
    if (meta) {
      hubs.push({ hubFolder: topDir, hubName: meta.hubName, hubType: meta.hubType });
    }
  }

  return hubs;
}

/**
 * Compute the hub folder slug with preview disambiguation.
 * Production: slugify(hubName)
 * Preview: slugify(hubName) + "--" + slugify(previewLabel || branchName || hubId[0:8])
 *
 * The double-hyphen join is unambiguous: slugifyKebab collapses internal hyphen
 * runs, so neither the base nor the suffix can contain "--".
 *
 * IMPORTANT: This must produce identical output to the backend's computeHubSlug
 * (apps/backend/src/routes/api/ci/lookup.ts) and getHubFolderSlug
 * (apps/backend/src/routes/api/files/index.ts). All three now resolve to the
 * SAME canonical implementation: `slugify` here is a re-export of @wayai/core's
 * `slugifySkillName` (= `slugifyKebab(name, 64)`), which the backend imports
 * directly — no separate copy to keep in lockstep.
 */
export function getHubFolderSlug(
  hubName: string,
  hubEnvironment: string,
  hubId: string,
  previewLabel?: string | null,
  branchName?: string | null
): string {
  const base = slugify(hubName);
  if (hubEnvironment !== 'preview') return base;
  const suffix = previewLabel || branchName || hubId.slice(0, 8);
  return `${base}--${slugify(suffix)}`;
}

/**
 * Resolve a hub folder in the workspace by hub_id match (handles folder renames),
 * falling back to a flat slugified hub path with preview disambiguation.
 */
export function resolveHubFolder(
  workspaceDir: string,
  hubId: string | undefined,
  hubName: string,
  hubEnvironment: string,
  previewLabel?: string | null,
  branchName?: string | null
): string {
  // First: try to find by hub_id match (handles folder renames)
  if (hubId) {
    const allHubs = scanWorkspaceHubs(workspaceDir);
    const match = allHubs.find((h) => h.hubId === hubId);
    if (match) return match.hubFolder;
  }

  // Fallback: flat slugified path with preview disambiguation
  const hubFolderSlug = getHubFolderSlug(hubName, hubEnvironment, hubId || '', previewLabel, branchName);
  return path.join(workspaceDir, hubFolderSlug);
}

/**
 * Resolve the on-disk folder for a known hub_id by scanning the workspace.
 * Returns null when no workspace exists or the hub isn't materialized locally.
 */
export function findLocalHubFolder(hubId: string): string | null {
  const workspace = detectWorkspace();
  const gitRoot = findGitRoot();
  const workspaceDir = workspace?.workspaceDir ?? (gitRoot ? resolveLayout(gitRoot).hubsDir : null);
  if (!workspaceDir || !fs.existsSync(workspaceDir)) return null;
  const match = scanWorkspaceHubs(workspaceDir).find((h) => h.hubId === hubId);
  return match ? match.hubFolder : null;
}

/** Find a hub in `workspace/<hub>/` whose folder basename matches the given name. */
export function findHubByFolderName(
  workspaceDir: string,
  hubFolderName: string,
): HubLocation | null {
  const all = scanWorkspaceHubs(workspaceDir);
  return all.find((h) => path.basename(h.hubFolder) === hubFolderName) ?? null;
}

/**
 * Auto-rename a hub folder when the hub name has changed.
 * Compares current folder name against the expected slug and renames if different.
 * Returns the new folder path (or same path if no rename occurred).
 */
export function autoRenameHubFolder(
  currentFolder: string,
  hubName: string,
  hubEnvironment: string,
  hubId: string,
  previewLabel?: string | null,
  branchName?: string | null
): string {
  const expectedSlug = getHubFolderSlug(hubName, hubEnvironment, hubId, previewLabel, branchName);
  const currentSlug = path.basename(currentFolder);

  if (currentSlug === expectedSlug) return currentFolder;

  const targetFolder = path.join(path.dirname(currentFolder), expectedSlug);

  if (fs.existsSync(targetFolder)) {
    console.warn(`  Warning: skipping hub folder rename ${currentSlug} → ${expectedSlug} (target already exists)`);
    return currentFolder;
  }

  try {
    fs.renameSync(currentFolder, targetFolder);
  } catch (err) {
    console.warn(`  Warning: could not rename ${currentSlug} → ${expectedSlug}: ${err instanceof Error ? err.message : String(err)}`);
    return currentFolder;
  }
  console.log(`  Renamed hub folder ${currentSlug} → ${expectedSlug}`);
  console.log(`  Note: stage this rename in git before committing (e.g. git add -A)`);
  return targetFolder;
}

/**
 * Walk from `process.cwd()` up to (and including) `workspaceDir`, returning
 * the first directory whose `hub.yaml` (or legacy `wayai.yaml`) carries a
 * valid `hub_id`/`hub_environment`. Returns null if no enclosing hub folder
 * is found before reaching the workspace root.
 */
export function findEnclosingHubFolder(workspaceDir: string): HubLocation | null {
  let current = process.cwd();
  const stop = path.resolve(workspaceDir);
  // Bounded like `findEnclosingBaseFolder`: a cwd outside the workspace never walks up
  // to adopt some other directory's hub.yaml.
  while (isUnder(stop, current)) {
    const meta = readHubYaml(path.join(current, 'hub.yaml'))
              || readHubYaml(path.join(current, 'wayai.yaml'));
    if (meta) {
      return { hubFolder: current, hubId: meta.hubId, hubEnvironment: meta.hubEnvironment };
    }
    if (current === stop) return null;
    current = path.dirname(current);
  }
  return null;
}

/**
 * Like `findEnclosingHubFolder`, but for a NEW (idless) hub folder — one whose
 * `hub.yaml` has a `hub.name` but no `hub_id` yet. This is the enclosing-folder
 * counterpart that lets `wayai push` / `wayai create` target a from-scratch hub
 * when run from inside its folder (id-bearing resolution is blind to idless
 * folders — see gh #3117). Returns null if no enclosing new-hub folder is found
 * before reaching the workspace root.
 */
export function findEnclosingNewHubFolder(workspaceDir: string): NewHubLocation | null {
  let current = process.cwd();
  const stop = path.resolve(workspaceDir);
  while (isUnder(stop, current)) {
    const meta = readNewHubYaml(path.join(current, 'hub.yaml'))
              || readNewHubYaml(path.join(current, 'wayai.yaml'));
    if (meta) {
      return { hubFolder: current, hubName: meta.hubName, hubType: meta.hubType };
    }
    if (current === stop) return null;
    current = path.dirname(current);
  }
  return null;
}

/** A resolved target for `wayai push` — an existing (created) hub or a new (idless) folder. */
export type PushTarget =
  | { kind: 'existing'; hub: HubLocation }
  | { kind: 'new'; hub: NewHubLocation };

/**
 * Resolution outcome for `wayai push`. On `ok:false` the `reason` tells the caller
 * which message + candidate list to print:
 *   - `selector_miss` — a `--hub <sel>` was given but matched no existing OR new folder
 *   - `none`          — the workspace has no hub folders at all
 *   - `ambiguous`     — no selector, and >1 candidate (existing previews + new folders)
 */
export type PushTargetResolution =
  | { ok: true; target: PushTarget }
  | { ok: false; reason: 'selector_miss' | 'none' | 'ambiguous' };

/**
 * Resolve which hub `wayai push` targets, treating created (id-bearing) and
 * from-scratch (idless) folders as CO-EQUAL candidates. This is the fix for gh
 * #3117: the previous resolver auto-selected the sole existing preview and never
 * saw idless folders, so a 1-existing-preview + 1-new-folder workspace silently
 * skipped the new hub. Resolution order:
 *   1. `--hub <sel>` — match existing (by id or folder name), then new (by folder
 *      name). A miss returns `selector_miss` — NOT a silent fall-through to
 *      auto-select (which is what dead-ended the new-hub create path before).
 *   2. cwd inside a hub folder — existing (id-bearing) OR new (idless).
 *   3. auto-select — exactly one candidate across `{preview existing} ∪ {new}`.
 *      More than one → `ambiguous` (require `--hub`); zero → `none`.
 * Read-only production mirrors are dropped from the auto-select candidate set
 * (push can't target production), matching `selectExistingHub`. Explicit `--hub`
 * still addresses a mirror by id/name so the caller can reject it with context.
 */
export function resolvePushTarget(
  workspaceDir: string,
  existingHubs: HubLocation[],
  newHubs: NewHubLocation[],
  selector: string | undefined,
): PushTargetResolution {
  // An empty selector (an unset variable) names nothing: refused below as a miss, never read as none.
  if (selector !== undefined) {
    const existingMatch = existingHubs.find(
      (h) => h.hubId === selector || path.basename(h.hubFolder) === selector,
    );
    if (existingMatch) return { ok: true, target: { kind: 'existing', hub: existingMatch } };
    const newMatch = newHubs.find((h) => path.basename(h.hubFolder) === selector);
    if (newMatch) return { ok: true, target: { kind: 'new', hub: newMatch } };
    return { ok: false, reason: 'selector_miss' };
  }

  const enclosingExisting = findEnclosingHubFolder(workspaceDir);
  if (enclosingExisting) return { ok: true, target: { kind: 'existing', hub: enclosingExisting } };
  const enclosingNew = findEnclosingNewHubFolder(workspaceDir);
  if (enclosingNew) return { ok: true, target: { kind: 'new', hub: enclosingNew } };

  const previewHubs = filterPreviewHubs(existingHubs);
  const total = previewHubs.length + newHubs.length;
  if (total === 0) return { ok: false, reason: 'none' };
  if (total > 1) return { ok: false, reason: 'ambiguous' };
  return previewHubs.length === 1
    ? { ok: true, target: { kind: 'existing', hub: previewHubs[0] } }
    : { ok: true, target: { kind: 'new', hub: newHubs[0] } };
}

/**
 * Resolution outcome for `wayai create` (only new/idless folders are targetable).
 * `exists` carries the matched existing hub so the caller can point at `wayai push`.
 */
export type NewHubResolution =
  | { ok: true; hub: NewHubLocation }
  | { ok: false; reason: 'exists'; existing: HubLocation }
  | { ok: false; reason: 'selector_miss' | 'none' | 'ambiguous' };

/**
 * Resolve which idless folder `wayai create` provisions. Unlike `resolvePushTarget`
 * it targets ONLY new hubs — a selector that names an already-created hub returns
 * `exists` (the caller redirects to `wayai push`), never silently re-creates.
 * `reason` mirrors `resolvePushTarget`'s vocabulary so the caller branches on it
 * without re-inspecting the raw selector:
 *   1. `<folder>` positional — a new folder; else an existing hub → `exists`;
 *      else `selector_miss`.
 *   2. cwd inside a new (idless) folder.
 *   3. exactly one new folder in the workspace; >1 → `ambiguous`; 0 → `none`.
 */
export function resolveNewHubForCreate(
  workspaceDir: string,
  existingHubs: HubLocation[],
  newHubs: NewHubLocation[],
  selector: string | undefined,
): NewHubResolution {
  // An empty selector (an unset variable) names nothing: refused below as a miss, never read as none.
  if (selector !== undefined) {
    const newMatch = newHubs.find((h) => path.basename(h.hubFolder) === selector);
    if (newMatch) return { ok: true, hub: newMatch };
    const existingMatch = existingHubs.find(
      (h) => h.hubId === selector || path.basename(h.hubFolder) === selector,
    );
    if (existingMatch) return { ok: false, reason: 'exists', existing: existingMatch };
    return { ok: false, reason: 'selector_miss' };
  }

  const enclosingNew = findEnclosingNewHubFolder(workspaceDir);
  if (enclosingNew) return { ok: true, hub: enclosingNew };

  if (newHubs.length === 1) return { ok: true, hub: newHubs[0] };
  if (newHubs.length === 0) return { ok: false, reason: 'none' };
  return { ok: false, reason: 'ambiguous' };
}

/**
 * Resolve the active hub for a workspace-scoped command.
 *
 * Resolution order:
 *   1. `--hub <uuid|folder-name>` flag in args (and consumed from args[])
 *   2. cwd is inside a hub folder
 *   3. exactly one hub in workspace/
 *   4. otherwise: error and exit
 *
 * Returns the hub's `hub_id`. Mutates `args` to remove the consumed `--hub` pair.
 */
export function resolveActiveHubId(args: string[]): string {
  // Parse and remove --hub <value> from args
  let selector: string | undefined;
  const hubIdx = args.indexOf('--hub');
  if (hubIdx !== -1) {
    const next = args[hubIdx + 1];
    if (!next || next.startsWith('--')) {
      console.error('--hub requires a value (uuid or workspace folder name).');
      process.exit(1);
    }
    selector = next;
    args.splice(hubIdx, 2);
  }

  const workspace = detectWorkspace();
  const gitRoot = findGitRoot();
  const workspaceDir = workspace?.workspaceDir ?? (gitRoot ? resolveLayout(gitRoot).hubsDir : null);
  // Display label for messages: e.g. "wayai-ws/hubs" (new) or "workspace" (legacy).
  const wsLabel = hubsDirLabel(gitRoot);

  if (!workspaceDir || !fs.existsSync(workspaceDir)) {
    if (selector && UUID_RE.test(selector)) return selector;
    console.error(`No ${wsLabel}/ directory found. Run \`wayai pull --hub <uuid>\` to fetch a hub, or pass --hub <uuid>.`);
    process.exit(1);
  }

  const allHubs = scanWorkspaceHubs(workspaceDir);

  if (selector) {
    const match = allHubs.find((h) =>
      h.hubId === selector || path.basename(h.hubFolder) === selector,
    );
    if (match) return match.hubId;

    // Allow raw UUID even when not materialized locally — useful for read-only commands.
    if (UUID_RE.test(selector)) return selector;

    console.error(`No hub matching --hub ${selector} found in ${wsLabel}/.`);
    process.exit(1);
  }

  const enclosing = findEnclosingHubFolder(workspaceDir);
  if (enclosing) return enclosing.hubId;

  // Auto-select ignores read-only production mirror folders so a preview + its
  // mirror still resolves to the single preview.
  const previewHubs = filterPreviewHubs(allHubs);

  if (previewHubs.length === 1) return previewHubs[0].hubId;

  if (previewHubs.length === 0) {
    console.error(`No hub folders found in ${wsLabel}/. Run \`wayai pull --hub <uuid>\` to fetch a hub for the first time, or pass --hub <uuid>.`);
    process.exit(1);
  }

  console.error(`Multiple hubs in ${wsLabel}/. Pass --hub <uuid|folder-name> or run from inside a hub folder:`);
  for (const h of previewHubs) {
    console.error(`  ${path.basename(h.hubFolder)} (${h.hubId})`);
  }
  process.exit(1);
}

/**
 * The `hub_environment` of a locally-materialized hub folder (`preview` |
 * `production`), or null if the hub isn't in the workspace. Read from the folder's
 * `hub.yaml` — works for read-only production mirrors too. Callers that need the
 * environment for a hub that may not be local should fall back to the server.
 */
export function localHubEnvironment(hubId: string): string | null {
  const workspace = detectWorkspace();
  const gitRoot = findGitRoot();
  const workspaceDir = workspace?.workspaceDir ?? (gitRoot ? resolveLayout(gitRoot).hubsDir : null);
  if (!workspaceDir || !fs.existsSync(workspaceDir)) return null;
  return scanWorkspaceHubs(workspaceDir).find((h) => h.hubId === hubId)?.hubEnvironment ?? null;
}

function safeReaddir(dir: string): string[] {
  try {
    return fs.readdirSync(dir).filter((entry) => !entry.startsWith('.'));
  } catch {
    return [];
  }
}
